import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { qaApi } from "../../services/api/qaApi";
import { useUserContext } from "../../context/UserContext";
import FeedSidebar from "./FeedSidebar";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import { FaQuestionCircle, FaFilter } from "react-icons/fa";
import "./UnansweredQuestionsPage.css";

const UnansweredQuestionsPage = () => {
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();
  const initialSort = searchParams.get("sort") || "recent";
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortBy, setSortBy] = useState(initialSort);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const loadQuestions = useCallback(
    async (pageNum = 0, append = false) => {
      try {
        if (pageNum === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const response = await qaApi.getUnansweredQuestions({
          limit: 15,
          offset: pageNum * 15,
          sortBy,
        });

        if (response.data.success) {
          const newQuestions = response.data.questions || [];

          if (append) {
            setQuestions((prev) => [...prev, ...newQuestions]);
          } else {
            setQuestions(newQuestions);
          }

          setHasMore(newQuestions.length === 15);
        }
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sortBy]
  );

  useEffect(() => {
    setPage(0);
    loadQuestions(0, false);
  }, [sortBy, loadQuestions]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadQuestions(nextPage, true);
  };

  const handlePostUpdate = (postId, updates) => {
    setQuestions((prev) =>
      prev.map((q) => (q._id === postId ? { ...q, ...updates } : q))
    );
  };

  const handlePostDelete = (postId) => {
    setQuestions((prev) => prev.filter((q) => q._id !== postId));
  };

  if (!user) {
    return (
      <div className="unanswered-questions-page">
        <div className="uqp-container">
          <div className="uqp-auth-required">
            <h2>Please log in to view questions</h2>
            <Link to="/" className="uqp-login-btn">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unanswered-questions-page">
      <div className="uqp-layout">
      <div className="uqp-main">
        <div className="uqp-header">
          <div className="uqp-header-info">
            <h1>
              <FaQuestionCircle className="uqp-icon" /> Unanswered Questions
            </h1>
            <p>Help the community by answering these questions</p>
          </div>

          <div className="uqp-filters">
            <FaFilter className="uqp-filter-icon" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="uqp-sort-select"
            >
              <option value="recent">Most Recent</option>
              <option value="votes">Most Votes</option>
              <option value="bounty">Highest Bounty</option>
            </select>
          </div>
        </div>

        <div className="uqp-content">
          {loading ? (
            <div className="uqp-loading">
              {[...Array(5)].map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          ) : questions.length === 0 ? (
            <div className="uqp-empty">
              <FaQuestionCircle className="uqp-empty-icon" />
              <h3>No unanswered questions</h3>
              <p>All questions have been answered! Check back later.</p>
            </div>
          ) : (
            <>
              <div className="uqp-questions-list">
                {questions.map((question) => (
                  <FeedPost
                    key={question._id}
                    post={question}
                    onPostUpdate={handlePostUpdate}
                    onPostDelete={handlePostDelete}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="uqp-load-more">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="uqp-load-more-btn"
                  >
                    {loadingMore ? "Loading..." : "Load More Questions"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <FeedSidebar />
      </div>
    </div>
  );
};

export default UnansweredQuestionsPage;
